/**
 * AlbumUploadDialog
 *
 * Lets a user upload an entire album folder in one go:
 *   1. Pick a folder (webkitdirectory) or multi-select audio files
 *   2. Review / edit per-track metadata (title, track number)
 *   3. Set album-level metadata (album title, artist, cover, year, type)
 *   4. Upload all tracks to Blossom, then publish one kind-36493 event per track
 *   5. Publish a kind-36381 album event that references all track d-tags
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { Album, ArrowLeft, Check, ChevronDown, ChevronUp, Disc3, GripVertical, Loader2, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useUploadFile } from '@/hooks/useUploadFile';
import { useAppContext } from '@/hooks/useAppContext';
import { getEffectiveBlossomServers } from '@/lib/appBlossom';
import { parseTrackFilename, findMusicBrainzCover } from '@/lib/musicBrainz';
import { cn } from '@/lib/utils';
import type { MusicTrack } from '@/components/AudioPlayer';
import { BlossomUploader } from '@nostrify/nostrify/uploaders';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TrackEntry {
  /** stable key */
  key: string;
  file: File;
  title: string;
  trackNumber: number;
  /** upload result: url + imeta tags */
  audioUrl?: string;
  imetaTags?: string[][];
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
}

interface AlbumUploadDialogProps {
  onClose: () => void;
  onAlbumPublished?: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const AUDIO_EXTENSIONS = /\.(mp3|flac|wav|ogg|aac|m4a|opus|wma|aiff?|alac)$/i;

function slugify(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function sortByTrackNumber(entries: TrackEntry[]): TrackEntry[] {
  return [...entries].sort((a, b) => a.trackNumber - b.trackNumber);
}

/** Extract a leading track number like "01 " or "01 - " or "01." from a filename. */
function extractTrackNumber(filename: string): { number: number; rest: string } {
  const match = filename.replace(/\.[^/.]+$/, '').match(/^(\d{1,3})[.\s_-]+(.+)$/);
  if (match) return { number: parseInt(match[1], 10), rest: match[2].trim() };
  return { number: 0, rest: filename.replace(/\.[^/.]+$/, '') };
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function TrackRow({
  entry,
  index,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
}: {
  entry: TrackEntry;
  index: number;
  onChange: (key: string, changes: Partial<TrackEntry>) => void;
  onRemove: (key: string) => void;
  onMoveUp: (key: string) => void;
  onMoveDown: (key: string) => void;
  isFirst: boolean;
  isLast: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition',
        entry.status === 'done' && 'border-green-500/30 bg-green-500/5',
        entry.status === 'error' && 'border-red-500/30 bg-red-500/5',
        entry.status === 'uploading' && 'border-violet-400/40 bg-violet-500/8',
        entry.status === 'pending' && 'border-white/10 bg-white/3',
      )}
    >
      {/* Drag handle / reorder */}
      <div className="flex shrink-0 flex-col">
        <button
          type="button"
          disabled={isFirst}
          onClick={() => onMoveUp(entry.key)}
          className="text-white/25 hover:text-white/60 disabled:opacity-0"
          aria-label="Move up"
        >
          <ChevronUp className="size-3" />
        </button>
        <button
          type="button"
          disabled={isLast}
          onClick={() => onMoveDown(entry.key)}
          className="text-white/25 hover:text-white/60 disabled:opacity-0"
          aria-label="Move down"
        >
          <ChevronDown className="size-3" />
        </button>
      </div>

      {/* Track number */}
      <input
        type="number"
        min={1}
        max={999}
        value={entry.trackNumber || ''}
        onChange={(e) => onChange(entry.key, { trackNumber: parseInt(e.target.value, 10) || index + 1 })}
        className="w-12 shrink-0 rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-center text-xs text-white focus:outline-none focus:ring-1 focus:ring-violet-400"
        aria-label="Track number"
      />

      {/* Title */}
      <input
        type="text"
        value={entry.title}
        onChange={(e) => onChange(entry.key, { title: e.target.value })}
        className="min-w-0 flex-1 rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-sm text-white focus:outline-none focus:ring-1 focus:ring-violet-400"
        placeholder="Track title"
        aria-label="Track title"
      />

      {/* Status indicator */}
      <span className="shrink-0">
        {entry.status === 'uploading' && <Loader2 className="size-4 animate-spin text-violet-300" />}
        {entry.status === 'done' && <Check className="size-4 text-green-400" />}
        {entry.status === 'error' && (
          <span title={entry.error} className="text-xs text-red-400">✕</span>
        )}
      </span>

      {/* Remove */}
      {entry.status === 'pending' && (
        <button
          type="button"
          onClick={() => onRemove(entry.key)}
          className="shrink-0 text-white/25 hover:text-white/60"
          aria-label={`Remove ${entry.title}`}
        >
          <X className="size-4" />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main dialog
// ---------------------------------------------------------------------------

export function AlbumUploadDialog({ onClose, onAlbumPublished }: AlbumUploadDialogProps) {
  const [open, setOpen] = useState(true);
  const [step, setStep] = useState<'pick' | 'review' | 'uploading' | 'done'>('pick');

  // ── album-level fields ──────────────────────────────────────────────────
  const [albumTitle, setAlbumTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [year, setYear] = useState('');
  const [albumType, setAlbumType] = useState<'album' | 'ep' | 'single'>('album');
  const [description, setDescription] = useState('');
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverUrl, setCoverUrl] = useState('');
  const [coverPreview, setCoverPreview] = useState('');
  const [blossomServer, setBlossomServer] = useState('');
  const [coverLookup, setCoverLookup] = useState(false);

  // ── tracks ──────────────────────────────────────────────────────────────
  const [tracks, setTracks] = useState<TrackEntry[]>([]);

  // ── progress ────────────────────────────────────────────────────────────
  const [progressMsg, setProgressMsg] = useState('');
  const [progressPct, setProgressPct] = useState(0);
  const [publishError, setPublishError] = useState('');

  // webkitdirectory must be set imperatively — React strips unknown boolean
  // attributes when passed as JSX props/spreads.
  const folderInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (folderInputRef.current) {
      folderInputRef.current.setAttribute('webkitdirectory', '');
    }
  }, []);

  const { user } = useCurrentUser();
  const { config } = useAppContext();
  const { mutateAsync: publish } = useNostrPublish();
  const { mutateAsync: uploadFile } = useUploadFile(blossomServer || undefined);
  const blossomServers = getEffectiveBlossomServers(
    config.blossomServerMetadata,
    config.useAppBlossomServers,
  );

  // ── file picking ─────────────────────────────────────────────────────────

  const handleFiles = useCallback((rawFiles: File[]) => {
    const audioFiles = rawFiles
      .filter((f) => AUDIO_EXTENSIONS.test(f.name))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

    if (audioFiles.length === 0) return;

    // Auto-fill album title from folder name if available
    if (!albumTitle) {
      const pathParts = (audioFiles[0].webkitRelativePath ?? '').split('/');
      if (pathParts.length >= 2) setAlbumTitle(pathParts[0]);
    }

    const entries: TrackEntry[] = audioFiles.map((file, i) => {
      const { number, rest } = extractTrackNumber(file.name);
      const parsed = parseTrackFilename(rest);
      if (!artist && parsed.artist) setArtist(parsed.artist);
      return {
        key: `${file.name}-${file.lastModified}-${i}`,
        file,
        title: parsed.title || rest,
        trackNumber: number || i + 1,
        status: 'pending',
      };
    });

    setTracks(entries);
    setStep('review');
  }, [albumTitle, artist]);

  const handleCoverFile = (file: File | null) => {
    setCoverFile(file);
    if (file) setCoverPreview(URL.createObjectURL(file));
    else setCoverPreview('');
  };

  // ── track list mutations ─────────────────────────────────────────────────

  const updateTrack = (key: string, changes: Partial<TrackEntry>) => {
    setTracks((prev) => prev.map((t) => (t.key === key ? { ...t, ...changes } : t)));
  };

  const removeTrack = (key: string) => {
    setTracks((prev) => {
      const next = prev.filter((t) => t.key !== key);
      return next.map((t, i) => ({ ...t, trackNumber: t.trackNumber || i + 1 }));
    });
  };

  const moveTrack = (key: string, dir: 1 | -1) => {
    setTracks((prev) => {
      const idx = prev.findIndex((t) => t.key === key);
      if (idx < 0) return prev;
      const next = [...prev];
      const swapIdx = idx + dir;
      if (swapIdx < 0 || swapIdx >= next.length) return prev;
      [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
      // reassign track numbers based on new order
      return next.map((t, i) => ({ ...t, trackNumber: i + 1 }));
    });
  };

  // ── submit ──────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!user || !albumTitle.trim() || !artist.trim() || tracks.length === 0) return;
    setStep('uploading');
    setPublishError('');

    try {
      // 1. Upload cover if present
      setProgressMsg('Uploading cover art…');
      setProgressPct(2);
      let finalCoverUrl = coverUrl;
      if (coverFile) {
        const coverTags = await uploadFile(coverFile);
        finalCoverUrl = coverTags[0]?.[1] ?? '';
      }

      // 2. Upload each track file + publish kind 36493
      const publishedRefs: string[] = [];
      const total = tracks.length;

      for (let i = 0; i < tracks.length; i++) {
        const track = tracks[i];
        setProgressMsg(`Uploading track ${i + 1} of ${total}: ${track.title}…`);
        setProgressPct(5 + Math.round((i / total) * 85));
        setTracks((prev) =>
          prev.map((t) => (t.key === track.key ? { ...t, status: 'uploading' } : t)),
        );

        try {
          const audioTags = await uploadFile(track.file);
          const audioUrl = audioTags[0][1];
          const imeta = audioTags.map(([name, value]) => `${name} ${value}`);
          const dTag = `${slugify(albumTitle)}-${slugify(track.title)}-${Date.now()}-${i}`;

          const event = await publish({
            kind: 36493,
            content: '',
            tags: [
              ['d', dTag],
              ['title', track.title.trim()],
              ['artist', artist.trim()],
              ['album', albumTitle.trim()],
              ['track_number', String(track.trackNumber)],
              ...(finalCoverUrl ? [['image', finalCoverUrl]] : []),
              ['alt', 'Music track'],
              ['imeta', ...imeta],
            ],
          });

          publishedRefs.push(`36493:${user.pubkey}:${dTag}`);
          setTracks((prev) =>
            prev.map((t) =>
              t.key === track.key ? { ...t, status: 'done', audioUrl } : t,
            ),
          );
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Upload failed';
          setTracks((prev) =>
            prev.map((t) =>
              t.key === track.key ? { ...t, status: 'error', error: msg } : t,
            ),
          );
        }
      }

      // 3. Publish the album event (kind 36381)
      setProgressMsg('Publishing album…');
      setProgressPct(95);
      const albumDTag = `music/album/${slugify(albumTitle)}-${Date.now()}`;
      await publish({
        kind: 36381,
        content: description,
        tags: [
          ['d', albumDTag],
          ['title', albumTitle.trim()],
          ['artist', artist.trim()],
          ['album_type', albumType],
          ...(year.trim() ? [['year', year.trim()]] : []),
          ...(finalCoverUrl ? [['image', finalCoverUrl]] : []),
          ['alt', 'Music album'],
          ...publishedRefs.map((ref) => ['a', ref]),
        ],
      });

      setProgressMsg('Done!');
      setProgressPct(100);
      setStep('done');
      onAlbumPublished?.();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Something went wrong';
      setPublishError(msg);
      setStep('review');
    }
  };

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  // ── render ───────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="flex max-h-[92dvh] w-[calc(100vw-2rem)] flex-col overflow-hidden border-white/10 bg-[#1b1826] text-white sm:max-w-2xl">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Album className="size-5 text-violet-300" />
            Upload an album
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">

          {/* ── Step: Pick files ──────────────────────────────────────── */}
          {step === 'pick' && (
            <div className="space-y-5 py-2">
              <p className="text-sm text-white/55">
                Select your album folder or all the audio files at once. Tracks will be
                ordered by their file names automatically.
              </p>

              {/* Folder picker */}
              <label className="flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-white/15 bg-white/3 px-6 py-10 text-center transition hover:border-violet-400/50 hover:bg-violet-400/5">
                <Album className="size-10 text-violet-300/60" />
                <div>
                  <p className="font-medium text-white/80">Choose an album folder</p>
                  <p className="mt-1 text-sm text-white/40">
                    All audio files inside will be imported
                  </p>
                </div>
                <span className="rounded-full bg-violet-500/20 px-4 py-1.5 text-sm text-violet-200">
                  Browse folder
                </span>
                <input
                  ref={folderInputRef}
                  type="file"
                  accept="audio/*"
                  multiple
                  className="sr-only"
                  onChange={(e) => handleFiles(Array.from(e.target.files ?? []))}
                />
              </label>

              <div className="flex items-center gap-3 text-xs text-white/30">
                <div className="h-px flex-1 bg-white/10" />
                or
                <div className="h-px flex-1 bg-white/10" />
              </div>

              {/* Individual file picker */}
              <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-white/15 bg-white/3 px-4 py-3 text-sm text-white/55 transition hover:border-violet-400/50 hover:bg-violet-400/5">
                <Upload className="size-4 shrink-0 text-violet-300" />
                Select individual audio files
                <input
                  type="file"
                  accept="audio/*"
                  multiple
                  className="sr-only"
                  onChange={(e) => handleFiles(Array.from(e.target.files ?? []))}
                />
              </label>

              <p className="text-xs text-white/30">
                Supported: MP3, FLAC, WAV, OGG, AAC, M4A, OPUS and more.
              </p>
            </div>
          )}

          {/* ── Step: Review / edit ───────────────────────────────────── */}
          {(step === 'review' || step === 'uploading') && (
            <div className="space-y-6 py-2">

              {/* Album metadata */}
              <section className="space-y-4">
                <p className="text-xs font-medium uppercase tracking-wider text-violet-300">
                  Album details
                </p>

                {/* Cover + basic info */}
                <div className="flex gap-4">
                  {/* Cover preview / picker */}
                  <label className={cn(
                    'relative flex size-28 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-xl border border-dashed border-white/15 bg-white/4 transition hover:border-violet-400/50',
                    step === 'uploading' && 'pointer-events-none',
                  )}>
                    {coverPreview || coverUrl ? (
                      <img
                        src={coverPreview || coverUrl}
                        alt="Album cover"
                        className="size-full object-cover"
                      />
                    ) : (
                      <div className="flex flex-col items-center gap-1 text-white/30">
                        <Disc3 className="size-8" />
                        <span className="text-[10px]">Cover</span>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={(e) => handleCoverFile(e.target.files?.[0] ?? null)}
                    />
                  </label>

                  <div className="flex flex-1 flex-col gap-2.5">
                    <div className="grid gap-2 sm:grid-cols-2">
                      <div className="space-y-1">
                        <Label className="text-xs text-white/50">
                          Album title <span className="text-red-400">*</span>
                        </Label>
                        <Input
                          value={albumTitle}
                          onChange={(e) => setAlbumTitle(e.target.value)}
                          placeholder="My Album"
                          className="h-8 border-white/10 bg-white/5 text-sm text-white"
                          disabled={step === 'uploading'}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-white/50">
                          Artist <span className="text-red-400">*</span>
                        </Label>
                        <Input
                          value={artist}
                          onChange={(e) => setArtist(e.target.value)}
                          placeholder="Artist name"
                          className="h-8 border-white/10 bg-white/5 text-sm text-white"
                          disabled={step === 'uploading'}
                        />
                      </div>
                    </div>

                    <div className="grid gap-2 sm:grid-cols-2">
                      <div className="space-y-1">
                        <Label className="text-xs text-white/50">Release type</Label>
                        <select
                          value={albumType}
                          onChange={(e) => setAlbumType(e.target.value as 'album' | 'ep' | 'single')}
                          className="h-8 w-full rounded-lg border border-white/10 bg-white/5 px-2 text-sm text-white disabled:opacity-50"
                          disabled={step === 'uploading'}
                        >
                          <option value="album">Album</option>
                          <option value="ep">EP</option>
                          <option value="single">Single</option>
                        </select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-white/50">Year</Label>
                        <Input
                          value={year}
                          onChange={(e) => setYear(e.target.value)}
                          inputMode="numeric"
                          placeholder="2026"
                          className="h-8 border-white/10 bg-white/5 text-sm text-white"
                          disabled={step === 'uploading'}
                        />
                      </div>
                    </div>

                    {/* Cover URL */}
                    <div className="flex gap-2">
                      <Input
                        value={coverUrl}
                        onChange={(e) => { setCoverUrl(e.target.value); setCoverPreview(''); }}
                        placeholder="Or paste a cover URL"
                        className="h-8 flex-1 border-white/10 bg-white/5 text-xs text-white"
                        disabled={step === 'uploading'}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={coverLookup || step === 'uploading' || (!artist.trim() && !albumTitle.trim())}
                        onClick={() => {
                          setCoverLookup(true);
                          void findMusicBrainzCover(artist.trim() || albumTitle.trim(), albumTitle.trim())
                            .then((res) => { if (res) { setCoverUrl(res.coverUrl); setCoverPreview(''); } })
                            .catch(() => null)
                            .finally(() => setCoverLookup(false));
                        }}
                        className="h-8 shrink-0 border-white/10 bg-white/5 text-white hover:bg-white/10 hover:text-white"
                      >
                        {coverLookup ? <Loader2 className="size-3 animate-spin" /> : 'Find cover'}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Blossom server + description (collapsible feel via smaller type) */}
                <div className="grid gap-2 sm:grid-cols-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-white/50">Upload server</Label>
                    <select
                      value={blossomServer}
                      onChange={(e) => setBlossomServer(e.target.value)}
                      className="h-8 w-full rounded-lg border border-white/10 bg-white/5 px-2 text-sm text-black disabled:opacity-50"
                      disabled={step === 'uploading'}
                    >
                      <option value="">Automatic</option>
                      {blossomServers.map((s) => (
                        <option key={s} value={s}>
                          {s.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-white/50">Description</Label>
                    <Input
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Optional album notes"
                      className="h-8 border-white/10 bg-white/5 text-sm text-white"
                      disabled={step === 'uploading'}
                    />
                  </div>
                </div>
              </section>

              {/* Track list */}
              <section className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium uppercase tracking-wider text-violet-300">
                    Tracks
                  </p>
                  <Badge className="border-violet-300/20 bg-violet-300/10 text-violet-200">
                    {tracks.length} file{tracks.length !== 1 ? 's' : ''}
                  </Badge>
                </div>

                <div className="max-h-64 space-y-1 overflow-y-auto rounded-xl border border-white/8 bg-white/2 p-2">
                  {tracks.map((track, i) => (
                    <TrackRow
                      key={track.key}
                      entry={track}
                      index={i}
                      onChange={updateTrack}
                      onRemove={removeTrack}
                      onMoveUp={(key) => moveTrack(key, -1)}
                      onMoveDown={(key) => moveTrack(key, 1)}
                      isFirst={i === 0}
                      isLast={i === tracks.length - 1}
                    />
                  ))}
                </div>

                {step !== 'uploading' && (
                  <button
                    type="button"
                    onClick={() => setStep('pick')}
                    className="flex items-center gap-1.5 text-xs text-white/35 hover:text-white/60"
                  >
                    <ArrowLeft className="size-3" /> Change files
                  </button>
                )}
              </section>

              {/* Upload progress */}
              {step === 'uploading' && (
                <section className="space-y-2">
                  <div className="flex items-center justify-between text-xs text-white/50">
                    <span>{progressMsg}</span>
                    <span>{progressPct}%</span>
                  </div>
                  <Progress value={progressPct} className="h-2 bg-white/10" />
                </section>
              )}

              {/* Error */}
              {publishError && (
                <p className="rounded-lg border border-red-500/30 bg-red-500/8 px-3 py-2 text-sm text-red-400">
                  {publishError}
                </p>
              )}
            </div>
          )}

          {/* ── Step: Done ───────────────────────────────────────────── */}
          {step === 'done' && (
            <div className="flex flex-col items-center gap-4 py-10 text-center">
              <div className="flex size-16 items-center justify-center rounded-2xl bg-green-500/15 text-green-400">
                <Check className="size-8" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">{albumTitle} published!</h3>
                <p className="mt-1 text-sm text-white/50">
                  {tracks.filter((t) => t.status === 'done').length} of {tracks.length} tracks
                  uploaded and linked to your album.
                </p>
                {tracks.some((t) => t.status === 'error') && (
                  <p className="mt-2 text-sm text-yellow-400/80">
                    {tracks.filter((t) => t.status === 'error').length} track(s) failed to upload.
                    You can re-upload them individually from the Upload screen.
                  </p>
                )}
              </div>
              <Button
                onClick={handleClose}
                className="mt-2 bg-violet-500 text-white hover:bg-violet-400"
              >
                Done
              </Button>
            </div>
          )}
        </div>

        {/* ── Footer actions ─────────────────────────────────────────────── */}
        {(step === 'review') && (
          <div className="flex shrink-0 justify-end gap-3 border-t border-white/8 pt-4">
            <Button
              variant="ghost"
              onClick={handleClose}
              className="text-white/60 hover:bg-white/8 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={() => void handleSubmit()}
              disabled={!albumTitle.trim() || !artist.trim() || tracks.length === 0}
              className="gap-2 bg-violet-500 text-white hover:bg-violet-400 disabled:opacity-40"
            >
              <Upload className="size-4" />
              Upload {tracks.length} track{tracks.length !== 1 ? 's' : ''}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
